import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import {
  ULTRA_PLUS_UNIQUE_FIELDS,
  ULTRA_PLUS_UNIQUE_FIELD_KEYS,
  normalizeUltraPlusUniqueValue,
} from "../config/ultraPlusFields";

const labelByKey = ULTRA_PLUS_UNIQUE_FIELDS.reduce((acc, f) => {
  acc[f.key] = f.label;
  return acc;
}, {});

function findMatchingKey(record, normalizedTarget) {
  for (const key of ULTRA_PLUS_UNIQUE_FIELD_KEYS) {
    if (normalizeUltraPlusUniqueValue(record[key]) === normalizedTarget) {
      return key;
    }
  }
  return null;
}

/**
 * Returns { valid, conflicts: { [fieldKey]: message } }
 */
export async function validateUltraPlusUniqueFields(
  fieldValues,
  currentUserId
) {
  const conflicts = {};
  const submitted = ULTRA_PLUS_UNIQUE_FIELDS.map(({ key, label }) => ({
    key,
    label,
    normalized: normalizeUltraPlusUniqueValue(fieldValues[key]),
  })).filter((entry) => entry.normalized);

  const seenInForm = new Map();
  submitted.forEach(({ key, label, normalized }) => {
    if (seenInForm.has(normalized)) {
      conflicts[key] =
        "This value is already used in another unique field on this form.";
      return;
    }
    seenInForm.set(normalized, label);
  });

  if (Object.keys(conflicts).length > 0) {
    return { valid: false, conflicts };
  }

  const usersSnapshot = await getDocs(collection(db, "users"));
  const pendingSnapshot = await getDocs(
    query(
      collection(db, "ultraPlusApprovalRequests"),
      where("status", "==", "pending")
    )
  );

  for (const { key, label, normalized } of submitted) {
    if (conflicts[key]) continue;

    for (const userDoc of usersSnapshot.docs) {
      const data = userDoc.data();
      if (data.uid === currentUserId) continue;

      const matchKey = findMatchingKey(data, normalized);
      if (matchKey) {
        conflicts[key] =
          matchKey === key
            ? `This ${label} is already registered in the system.`
            : `This value is already used as ${labelByKey[matchKey]} by another customer.`;
        break;
      }
    }

    if (conflicts[key]) continue;

    for (const reqDoc of pendingSnapshot.docs) {
      const data = reqDoc.data();
      if (data.userId === currentUserId) continue;

      const matchKey = findMatchingKey(data, normalized);
      if (matchKey) {
        conflicts[key] =
          matchKey === key
            ? `This ${label} is already pending approval for another customer.`
            : `This value is already pending as ${labelByKey[matchKey]} for another customer.`;
        break;
      }
    }
  }

  return {
    valid: Object.keys(conflicts).length === 0,
    conflicts,
  };
}
